import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { SecretStatus } from '@sss/types';
import secretStyles from '../components/Secret/Secret.module.css';
import { usePageTitle } from '../hooks/usePageTitle';
import { readFragment } from '../lib/secretLinks';
import { SecretsApiError, deleteSecret, getSecretStatus } from '../lib/secretsApi';
import ui from '../styles/ui.module.css';
import styles from './RevealPage.module.css';

/**
 * /status/:id#<senderToken> — the sender's read receipt and "delete now".
 * The token stays in the fragment (this link is the sender's to keep) and
 * goes to the API only in an Authorization header.
 */

type State =
  | { step: 'loading' }
  | { step: 'incomplete' }
  | { step: 'unknown' }
  | { step: 'failed' }
  | { step: 'loaded'; status: SecretStatus };

export default function StatusPage() {
  const { t, i18n } = useTranslation();
  const { id = '' } = useParams();
  const [token] = useState(() => readFragment(window.location.hash));
  const [state, setState] = useState<State>({ step: 'loading' });
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  usePageTitle(t('pageTitle.status'));
  // Each step replaces the button that was pressed, so say where focus goes next.
  const heading = useRef<HTMLHeadingElement>(null);
  const deleteButton = useRef<HTMLButtonElement>(null);
  const confirmButton = useRef<HTMLButtonElement>(null);
  const [announce, setAnnounce] = useState('');

  const load = useCallback(async () => {
    if (!token) return setState({ step: 'incomplete' });
    try {
      setState({ step: 'loaded', status: await getSecretStatus(id, token) });
    } catch (err) {
      setState({ step: err instanceof SecretsApiError && err.type === 'gone' ? 'unknown' : 'failed' });
    }
  }, [id, token]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (confirming) confirmButton.current?.focus();
  }, [confirming]);

  async function onDelete() {
    if (!token) return;
    setDeleting(true);
    try {
      await deleteSecret(id, token);
    } catch {
      // Reloading the status below shows whether it worked.
    }
    setDeleting(false);
    setConfirming(false);
    await load();
    heading.current?.focus();
  }

  function onKeep() {
    setConfirming(false);
    // The delete button is back once React re-renders.
    requestAnimationFrame(() => deleteButton.current?.focus());
  }

  async function onRefresh() {
    setAnnounce('');
    await load();
    setAnnounce(t('status.refreshed'));
  }

  const when = (iso: string) => new Date(iso).toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' });

  return (
    <main className={styles.page}>
      <div className={styles.stack}>
        <p className={ui.eyebrow}>{t('status.eyebrow')}</p>
        {state.step === 'loading' && <p role="status">{t('status.loading')}</p>}
        {state.step === 'incomplete' && <h1 className={styles.title}>{t('status.incomplete')}</h1>}
        {state.step === 'unknown' && (
          <>
            <h1 className={styles.title}>{t('status.unknown.title')}</h1>
            <p className={styles.lead}>{t('status.unknown.body')}</p>
          </>
        )}
        {state.step === 'failed' && <h1 className={styles.title}>{t('status.failed')}</h1>}
        {state.step === 'loaded' && (
          <>
            <h1 className={styles.title} ref={heading} tabIndex={-1}>
              {t(`status.state.${state.status.state}`)}
            </h1>
            <dl className={secretStyles.facts}>
              <dt>{t('status.views')}</dt>
              <dd>{t('status.viewsValue', { used: state.status.maxViews - state.status.viewsLeft, max: state.status.maxViews })}</dd>
              <dt>{t('status.created')}</dt>
              <dd>{when(state.status.createdAt)}</dd>
              <dt>{state.status.state === 'active' ? t('status.expires') : t('status.ended')}</dt>
              <dd>{when(state.status.endedAt ?? state.status.expiresAt)}</dd>
            </dl>
            {state.status.viewedAt.length > 0 && (
              <section>
                <h2 className={ui.eyebrow}>{t('status.openedAt')}</h2>
                <ol>
                  {state.status.viewedAt.map((at, i) => (
                    <li key={`${at}-${i}`}>{when(at)}</li>
                  ))}
                </ol>
              </section>
            )}
            {state.status.state === 'active' &&
              (confirming ? (
                <div className={secretStyles.panel} role="group" aria-label={t('status.confirmLabel')}>
                  <p className={secretStyles.notice}>{t('status.confirm')}</p>
                  <div className={secretStyles.actions}>
                    <button type="button" className={secretStyles.dangerButton} onClick={onDelete} disabled={deleting} ref={confirmButton}>
                      {t('status.deleteNow')}
                    </button>
                    <button type="button" className={ui.buttonSecondary} onClick={onKeep}>
                      {t('status.keep')}
                    </button>
                  </div>
                </div>
              ) : (
                <button type="button" className={secretStyles.dangerButton} onClick={() => setConfirming(true)} ref={deleteButton}>
                  {t('status.delete')}
                </button>
              ))}
            <button type="button" className={ui.buttonSecondary} onClick={() => void onRefresh()}>
              {t('status.refresh')}
            </button>
            <p role="status" className="visually-hidden">
              {announce}
            </p>
            <p className={ui.hint}>{t('status.retention')}</p>
          </>
        )}
      </div>
    </main>
  );
}
